import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../lib/api";
import AdminSpeakerReviewView from "./AdminSpeakerReviewView";

const ADMIN_KEY = import.meta.env.VITE_ADMIN_KEY;

type SpeakersTab = "review" | "unnamed" | "maintenance";

export default function AdminSpeakersView() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<SpeakersTab>("review");
  const [toastMessage, setToastMessage] = useState("");

  // Unnamed speakers state
  const [speakersData, setSpeakersData] = useState<{
    items: {
      id: string;
      title: string;
      channel_name: string;
      video_id: string;
      unnamed_speakers: { id: string; name: string; role?: string }[];
      named_speakers: string[];
    }[];
    top_channels_with_unnamed: { channel: string; count: number }[];
  } | null>(null);
  const [speakersLoading, setSpeakersLoading] = useState(false);
  const [speakersChannel, setSpeakersChannel] = useState("");

  // Speaker Maintenance state
  const [maintenanceData, setMaintenanceData] = useState<{
    video_id: string;
    title: string;
    channel_name: string;
    unnamed_count: number;
    total_speakers: number;
  }[]>([]);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [fixingVideoId, setFixingVideoId] = useState<string | null>(null);
  const [batchRunning, setBatchRunning] = useState(false);

  // Terminal unidentifiable count (from counters endpoint)
  const [terminalCount, setTerminalCount] = useState<number | null>(null);

  // Toast auto-dismiss
  useEffect(() => {
    if (!toastMessage) return;
    const t = setTimeout(() => setToastMessage(""), 3000);
    return () => clearTimeout(t);
  }, [toastMessage]);

  // Fetch unnamed speakers when Unnamed tab is active
  useEffect(() => {
    if (activeTab !== "unnamed") return;
    setSpeakersLoading(true);
    const url = speakersChannel
      ? `${API_BASE}/admin/speakers/unnamed?limit=100&channel=${encodeURIComponent(speakersChannel)}`
      : `${API_BASE}/admin/speakers/unnamed?limit=100`;
    fetch(url, { headers: { "X-Admin-Key": ADMIN_KEY } })
      .then((res) => res.json())
      .then((data) => setSpeakersData(data))
      .catch((err) => console.error("Failed to load speakers:", err))
      .finally(() => setSpeakersLoading(false));
  }, [activeTab, speakersChannel]);

  // Fetch terminal count from counters endpoint when Review tab is active
  useEffect(() => {
    if (activeTab !== "review") return;
    fetch(`${API_BASE}/admin/speaker-audit/counters`, {
      headers: { "X-Admin-Key": ADMIN_KEY },
    })
      .then((r) => r.json())
      .then((d) =>
        setTerminalCount(
          typeof d.terminal_unidentifiable === "number" ? d.terminal_unidentifiable : null
        )
      )
      .catch(() => {});
  }, [activeTab]);

  // Fetch speaker maintenance data
  const fetchMaintenanceData = useCallback(async () => {
    setMaintenanceLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/speakers/unnamed?limit=50`, {
        headers: { "X-Admin-Key": ADMIN_KEY },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setMaintenanceData(data.transcripts || data.items || data || []);
    } catch (err) {
      console.error("Failed to load maintenance data:", err);
      setToastMessage("Failed to load speaker maintenance data");
    } finally {
      setMaintenanceLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== "maintenance") return;
    fetchMaintenanceData();
  }, [activeTab, fetchMaintenanceData]);

  // Fix single transcript speakers
  const handleFixSpeakers = useCallback(async (videoId: string) => {
    setFixingVideoId(videoId);
    try {
      const res = await fetch(`${API_BASE}/admin/speakers/rename/${videoId}`, {
        method: "POST",
        headers: { "X-Admin-Key": ADMIN_KEY },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      setToastMessage("Speaker naming completed successfully");
      fetchMaintenanceData();
    } catch (err) {
      setToastMessage(`Failed: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setFixingVideoId(null);
    }
  }, [fetchMaintenanceData]);

  // Run batch maintenance
  const handleBatchMaintenance = useCallback(async () => {
    setBatchRunning(true);
    try {
      const res = await fetch(`${API_BASE}/admin/speakers/maintenance?max_transcripts=10`, {
        method: "POST",
        headers: { "X-Admin-Key": ADMIN_KEY },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      const result = await res.json();
      const processed = result.processed || result.count || 0;
      setToastMessage(`Batch maintenance completed: ${processed} transcripts processed`);
      fetchMaintenanceData();
    } catch (err) {
      setToastMessage(`Batch failed: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setBatchRunning(false);
    }
  }, [fetchMaintenanceData]);

  const tabs: { id: SpeakersTab; label: string }[] = [
    { id: "review", label: "Review Queue" },
    { id: "unnamed", label: "Unnamed" },
    { id: "maintenance", label: "Maintenance" },
  ];

  return (
    <div className="space-y-4">
      {/* Tab bar */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab: Review Queue */}
      {activeTab === "review" && (
        <div className="space-y-6">
          <AdminSpeakerReviewView />

          {/* Terminal: unidentifiable — collapsed by default */}
          <details className="rounded-lg border border-gray-200 bg-gray-50">
            <summary className="cursor-pointer flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg select-none list-none">
              <span>Terminal: unidentifiable</span>
              {terminalCount !== null && (
                <span className="inline-flex items-center rounded-full bg-gray-200 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                  {terminalCount}
                </span>
              )}
            </summary>
            <div className="px-4 pb-4 pt-3 border-t border-gray-200">
              <p className="text-sm text-gray-500 mb-3">
                Flags marked reviewed with no corrected speaker and{" "}
                <code className="text-xs bg-gray-200 px-1 rounded">failure_mode='unnamed'</code>
                {" "}— confirmed unresolvable cases. Read-only audit trail.
              </p>
              {terminalCount === null ? (
                <p className="text-sm text-gray-400">Loading…</p>
              ) : terminalCount === 0 ? (
                <p className="text-sm text-green-600 font-medium">
                  ✓ No confirmed-unresolvable speakers
                </p>
              ) : (
                <div className="rounded-md border border-gray-200 bg-white px-4 py-3">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">{terminalCount}</span> confirmed-unresolvable
                    case{terminalCount !== 1 ? "s" : ""} on record.
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    Per-episode breakdown requires a{" "}
                    <code className="bg-gray-100 px-1 rounded">/admin/speaker-audit/terminal</code>{" "}
                    list endpoint (not yet implemented — flag to Morpheus if needed).
                  </p>
                </div>
              )}
            </div>
          </details>
        </div>
      )}

      {/* Tab: Unnamed */}
      {activeTab === "unnamed" && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <h1 className="text-xl font-semibold text-gray-900">Unnamed Speakers</h1>
            <input
              type="text"
              placeholder="Filter by channel..."
              value={speakersChannel}
              onChange={(e) => setSpeakersChannel(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            />
          </div>

          {/* Top channels with unnamed speakers */}
          {speakersData?.top_channels_with_unnamed && speakersData.top_channels_with_unnamed.length > 0 && (
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Channels with Most Unnamed Speakers</h3>
              <div className="flex flex-wrap gap-2">
                {speakersData.top_channels_with_unnamed.map((ch) => (
                  <button
                    key={ch.channel}
                    onClick={() => setSpeakersChannel(ch.channel || "")}
                    className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-200"
                  >
                    {ch.channel || "Unknown"}
                    <span className="rounded-full bg-red-100 px-1.5 text-red-700">{ch.count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {speakersLoading ? (
            <div className="flex h-64 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
            </div>
          ) : speakersData?.items && speakersData.items.length > 0 ? (
            <div className="space-y-3">
              {speakersData.items.map((item) => (
                <div key={item.id} className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <h4 className="truncate text-sm font-medium text-gray-900">{item.title}</h4>
                      <p className="text-xs text-gray-500">{item.channel_name}</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {item.unnamed_speakers.map((s, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700"
                          >
                            {s.name || s.id}
                            {s.role && <span className="ml-1 text-red-500">({s.role})</span>}
                          </span>
                        ))}
                        {item.named_speakers.map((name, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700"
                          >
                            {name}
                          </span>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={() => navigate(`/transcript/${item.id}`)}
                      className="shrink-0 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
                    >
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex h-64 items-center justify-center">
              <div className="text-center">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="mx-auto h-12 w-12 text-green-300">
                  <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                </svg>
                <p className="mt-4 text-sm font-medium text-gray-500">
                  {speakersChannel ? "No unnamed speakers in this channel" : "All speakers are named!"}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab: Maintenance */}
      {activeTab === "maintenance" && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h1 className="text-xl font-semibold text-gray-900">Speaker Maintenance</h1>
            <button
              onClick={handleBatchMaintenance}
              disabled={batchRunning || maintenanceLoading}
              className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {batchRunning ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  Running...
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                    <path fillRule="evenodd" d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0v2.43l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389 5.5 5.5 0 0 1 9.2-2.466l.312.311h-2.433a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.22Z" clipRule="evenodd" />
                  </svg>
                  Run Batch (10)
                </>
              )}
            </button>
          </div>

          {/* Stats card */}
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5 text-blue-600">
                  <path d="M10 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM3.465 14.493a1.23 1.23 0 0 0 .41 1.412A9.957 9.957 0 0 0 10 18c2.31 0 4.438-.784 6.131-2.1.43-.333.604-.903.408-1.41a7.002 7.002 0 0 0-13.074.003Z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-blue-900">
                  {maintenanceData.length} transcripts with unnamed speakers
                </p>
                <p className="text-xs text-blue-700">
                  Click "Fix" to re-run speaker naming, or use batch to process multiple
                </p>
              </div>
            </div>
          </div>

          {maintenanceLoading ? (
            <div className="flex h-64 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
            </div>
          ) : maintenanceData.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Video
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Title
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Channel
                    </th>
                    <th className="px-4 py-2 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      Speakers
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {maintenanceData.map((item) => (
                    <tr key={item.video_id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-4 py-2">
                        <a
                          href={`https://youtube.com/watch?v=${item.video_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-mono text-blue-600 hover:underline"
                        >
                          {item.video_id.slice(0, 11)}
                        </a>
                      </td>
                      <td className="max-w-xs truncate px-4 py-2 text-sm text-gray-900">
                        {item.title}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-500">
                        {item.channel_name || "-"}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-center">
                        <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                          {item.unnamed_count} / {item.total_speakers}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-right">
                        <div className="inline-flex items-center gap-2">
                          {item.video_id && !item.video_id.startsWith("rss_") && (
                            <a
                              href={`https://www.youtube.com/watch?v=${item.video_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center rounded-md border border-gray-300 p-1.5 text-red-500 hover:bg-gray-50 hover:text-red-600 transition-colors"
                              title="View on YouTube"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                              </svg>
                            </a>
                          )}
                          <button
                            onClick={() => handleFixSpeakers(item.video_id)}
                            disabled={fixingVideoId === item.video_id}
                            className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                          >
                            {fixingVideoId === item.video_id ? (
                              <>
                                <div className="h-3 w-3 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                Fixing...
                              </>
                            ) : (
                              "Fix"
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex h-64 items-center justify-center">
              <div className="text-center">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="mx-auto h-12 w-12 text-green-300">
                  <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                </svg>
                <p className="mt-4 text-sm font-medium text-gray-500">
                  All speakers are named!
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  No transcripts need speaker maintenance
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Toast */}
      {toastMessage && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-gray-900 px-4 py-2.5 text-sm text-white shadow-lg">
          {toastMessage}
        </div>
      )}
    </div>
  );
}
